import {
  updateBusinessEntity,
  updateStockClass,
  useAllRegisteredAgents,
  useStockClasses,
} from '@commenda/api';
import {
  Button,
  DatePicker,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  FieldWrapper,
  Input,
  Label,
  Loading,
} from '@commenda/kakashi';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@commenda/kakashi/src/components/Select/Select';
import { ServiceRequestDetails } from '@commenda/types/src/service-request/types';
import { getPrintableAddress } from '@commenda/utils';
import { zodResolver } from '@hookform/resolvers/zod';
import { Dispatch, SetStateAction } from 'react';
import { useForm } from 'react-hook-form';
import { KeyedMutator } from 'swr';
import { useNotification } from '../../../../hooks/useNotification';
import {
  IncorporationDetailsSchema,
  IncorporationDetailsSchemaFields,
} from '../schema/incorporation-details-schema';

type IncorporationDetailModalProps = {
  isOpen: boolean;
  setIsOpen: Dispatch<SetStateAction<boolean>>;
  onConfirm: (
    formData: IncorporationDetailsSchemaFields & {
      registeredAgentAddress: string;
    }
  ) => void;
  serviceRequest: ServiceRequestDetails;
  mutateServiceRequest: KeyedMutator<unknown>;
};

export const IncorporationDetailsModal = ({
  isOpen,
  setIsOpen,
  onConfirm,
  serviceRequest,
  mutateServiceRequest,
}: IncorporationDetailModalProps) => {
  const { setNotification } = useNotification();

  const { data: registeredAgentList, error: registeredAgentError } =
    useAllRegisteredAgents(undefined);

  const {
    handleSubmit,
    register,
    formState: { errors, isSubmitting },
    setValue,
    watch,
    reset,
    clearErrors,
  } = useForm<IncorporationDetailsSchemaFields>({
    resolver: zodResolver(IncorporationDetailsSchema),
    defaultValues: {
      formationDate: null,
      corporationName: '',
      shareCount: null,
      uniqueIdentifierFromJurisdiction: null,
      registeredAgentId: null,
    },
  });

  const {
    data: stockClasses,
    error: stockClassesError,
    mutate: mutateStockClasses,
  } = useStockClasses(serviceRequest.corporationId);

  const stockClass = stockClasses?.[0];

  const onSubmit = async (formData: IncorporationDetailsSchemaFields) => {
    const registeredAgent = registeredAgentList.find(
      (each) => each.id === formData.registeredAgentId
    );
    if (!registeredAgent) {
      setNotification({
        show: true,
        valid: false,
        title: 'Error',
        message: 'Registered agent not found.',
      });
      return;
    }

    const updateCorporationRes = await updateBusinessEntity(
      serviceRequest.corporationId,
      {
        legalName: formData.corporationName,
        ...(formData.formationDate
          ? { formationDate: formData.formationDate }
          : {}),
        uniqueIdentifierFromJurisdiction:
          formData.uniqueIdentifierFromJurisdiction
            ? formData.uniqueIdentifierFromJurisdiction.toString()
            : null,
      }
    );

    if (!updateCorporationRes.ok) {
      setNotification({
        show: true,
        valid: false,
        title: 'Error',
        message: 'Failed to update the incorporation details.',
      });
      return;
    }

    const updateStockClassRes = await updateStockClass(stockClass.id, {
      additionalInformation: {
        authorizedShares: +formData.shareCount,
      },
    });

    if (!updateStockClassRes.ok) {
      setNotification({
        show: true,
        valid: false,
        title: 'Error',
        message: 'Failed to update the incorporation details.',
      });
      return;
    }

    onConfirm({
      ...formData,
      registeredAgentAddress: getPrintableAddress(registeredAgent.address),
    });
    await mutateStockClasses();
    await mutateServiceRequest();
    reset();
    setIsOpen(false);
  };

  if (
    !stockClass ||
    !registeredAgentList ||
    stockClassesError ||
    registeredAgentError
  ) {
    if (stockClassesError) console.error(stockClassesError);

    return (
      <Dialog
        open={isOpen}
        onOpenChange={(val) => {
          setIsOpen(val);
          reset();
        }}
      >
        <DialogContent>
          <Loading />
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(val) => {
        setIsOpen(val);
        reset();
      }}
    >
      <DialogContent
        className="p-0 flex flex-col gap-0"
        preventCloseOnOutsideClick
      >
        <DialogHeader className="flex flex-col justify-start !items-start">
          <DialogTitle>Enter incorporation details</DialogTitle>
          <p className="text-sm text-text-tertiary">
            These details will be saved in the client's profile
          </p>
        </DialogHeader>
        <form
          onSubmit={handleSubmit(onSubmit, (formError: unknown) =>
            console.log(formError)
          )}
        >
          <div className="p-6 flex flex-col gap-5">
            <FieldWrapper>
              <Label>Date of incorporation*</Label>
              <DatePicker
                date={watch('formationDate')}
                setDate={(value) => {
                  setValue('formationDate', value);
                }}
                hasError={!!errors?.formationDate?.message}
                {...register('formationDate')}
                validateDate={(date) => {
                  if (new Date(date) > new Date()) {
                    setNotification({
                      show: true,
                      valid: false,
                      title: 'Error',
                      message: 'Date of incorporation cannot be in the future.',
                    });
                    return false;
                  }
                  return true;
                }}
                disabled={[{ after: new Date() }]}
              />
            </FieldWrapper>
            <FieldWrapper>
              <Label>Entity's final name*</Label>
              <Input
                id="corporationName"
                {...register('corporationName')}
                error={errors.corporationName?.message}
                defaultValue={watch('corporationName') || ''}
                placeholder="Ex: Commenda Pvt. Ltd."
              />
            </FieldWrapper>
            <FieldWrapper>
              <Label>Total no. of shares*</Label>
              <Input
                id="shareCount"
                onChange={(e) => {
                  const { value } = e.target;
                  if (value === '' || !Number.isNaN(Number(value))) {
                    setValue('shareCount', Number(value));
                  }
                }}
                error={errors.shareCount?.message}
                defaultValue={watch('shareCount')}
                placeholder="Total shares issued"
                type="number"
              />
            </FieldWrapper>
            <FieldWrapper>
              <Label>Corporation entity number</Label>
              <Input
                id="uniqueIdentifierFromJurisdiction"
                value={watch('uniqueIdentifierFromJurisdiction')}
                onChange={(e) => {
                  const { value } = e.target;
                  if (value === '' || !Number.isNaN(Number(value))) {
                    setValue('uniqueIdentifierFromJurisdiction', Number(value));
                  }
                }}
                error={errors.uniqueIdentifierFromJurisdiction?.message}
                defaultValue={watch('uniqueIdentifierFromJurisdiction')}
                placeholder="Corporation Entity Number"
                type="number"
              />
            </FieldWrapper>
            <FieldWrapper>
              <Label>Address of registered agent*</Label>
              <Select
                onValueChange={(value) => {
                  clearErrors('registeredAgentId');
                  setValue('registeredAgentId', value);
                }}
                value={watch('registeredAgentId')}
              >
                <SelectTrigger error={errors.registeredAgentId?.message}>
                  <SelectValue placeholder="Select registered agent" />
                </SelectTrigger>
                <SelectContent>
                  {registeredAgentList?.map((each) => (
                    <SelectItem
                      key={each?.id}
                      value={each?.id}
                      className="w-full"
                    >
                      <div className="w-full max-w-md">
                        <p className="truncate">
                          {each?.address
                            ? getPrintableAddress(each?.address)
                            : 'No Registered agent avaialable'}
                        </p>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FieldWrapper>
          </div>
          <DialogFooter>
            <Button
              variant="secondary"
              type="button"
              onClick={() => {
                setIsOpen(false);
                reset();
              }}
            >
              Cancel
            </Button>
            <Button disabled={isSubmitting} type="submit">
              Save Details
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
